import { diffArrays } from 'diff';
import type { AgentSopDto, AgentSopStep, AgentSopVersionEntry } from '@erp/rpc';

const STEP_FIELDS = [
  'name',
  'description',
  'constraints',
  'kind',
  'phase',
  'risk',
  'provenance',
  'authorship',
  'surface',
  'target',
  'action',
  'verify',
  'branch',
  'refSopId',
  'refVersion',
  'paramBindings',
  'sensitive',
  'bestEffort',
] as const satisfies readonly (keyof AgentSopStep)[];
export type DiffFieldKey =
  | (typeof STEP_FIELDS)[number]
  | 'sessionRules'
  | 'parameter'
  | 'bodyMarkdown'
  | 'code';
export interface SopDiffField {
  key: DiffFieldKey;
  before: string;
  after: string;
  changed: boolean;
}
export interface SopDiffSection {
  id: string;
  name: string;
  kind: 'step' | 'parameter' | 'bodyMarkdown' | 'code' | 'sessionRules';
  status: 'added' | 'removed' | 'modified' | 'unchanged';
  moved: boolean;
  beforePosition?: number;
  afterPosition?: number;
  fields: SopDiffField[];
}
export type SopDiffSnapshot = {
  steps: Array<Pick<AgentSopStep, 'stepId' | 'name'> & Partial<AgentSopStep>>;
  parameters: AgentSopDto['parameters'];
  bodyMarkdown: string;
  sessionRules?: string[];
};

/** Stable object order, meaningful array order, and literal code whitespace. */
function canonicalValue(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(canonicalValue);
  if (value !== null && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([key, child]) => [key, canonicalValue(child)]),
    );
  }
  return value;
}
function actionMetadata(action: AgentSopStep['action'] | undefined): unknown {
  if (!action) return action;
  const { code: _code, ...metadata } = action;
  return metadata;
}
function displayValue(value: unknown): string {
  if (value === undefined) return '';
  if (typeof value === 'string') return value;
  return JSON.stringify(canonicalValue(value), null, 2) ?? '';
}
function field(
  key: DiffFieldKey,
  before: unknown,
  after: unknown,
): SopDiffField {
  const left = displayValue(before);
  const right = displayValue(after);
  return { key, before: left, after: right, changed: left !== right };
}

/** LCS of shared identities prevents an insertion from moving every following row. */
function movedIds(before: string[], after: string[]): Set<string> {
  const left = new Set(before);
  const right = new Set(after);
  const changes = diffArrays(
    before.filter((id) => right.has(id)),
    after.filter((id) => left.has(id)),
  );
  return new Set(
    changes.flatMap((change) => (change.added ? change.value : [])),
  );
}
function status(
  before: unknown,
  after: unknown,
  fields: SopDiffField[],
): SopDiffSection['status'] {
  if (before === undefined) return 'added';
  if (after === undefined) return 'removed';
  return fields.some((entry) => entry.changed) ? 'modified' : 'unchanged';
}

function compareList<T>(
  before: T[],
  after: T[],
  identity: (item: T) => string,
  describe: (
    before: T | undefined,
    after: T | undefined,
  ) => Pick<SopDiffSection, 'kind' | 'name' | 'fields'>,
): SopDiffSection[] {
  const left = new Map(
    before.map((item, index) => [
      identity(item),
      { item, position: index + 1 },
    ]),
  );
  const right = new Map(
    after.map((item, index) => [identity(item), { item, position: index + 1 }]),
  );
  const moved = movedIds([...left.keys()], [...right.keys()]);
  // Target order is authoritative; removed rows follow, retaining their old positions.
  const ids = [
    ...right.keys(),
    ...[...left.keys()].filter((id) => !right.has(id)),
  ];
  return ids.map((id) => {
    const oldEntry = left.get(id);
    const newEntry = right.get(id);
    const content = describe(oldEntry?.item, newEntry?.item);
    return {
      ...content,
      id: `${content.kind}:${id}`,
      status: status(oldEntry, newEntry, content.fields),
      moved: moved.has(id),
      beforePosition: oldEntry?.position,
      afterPosition: newEntry?.position,
    };
  });
}
export function buildSopVersionDiff(
  before: SopDiffSnapshot,
  after: SopDiffSnapshot,
): SopDiffSection[] {
  const steps = compareList(
    before.steps,
    after.steps,
    (step) => step.stepId,
    (left, right) => ({
      kind: 'step',
      name: right?.name ?? left?.name ?? '',
      fields: [
        ...STEP_FIELDS.map((key) =>
          key === 'action'
            ? field(
                key,
                actionMetadata(left?.action),
                actionMetadata(right?.action),
              )
            : field(key, left?.[key], right?.[key]),
        ),
        field('code', left?.action?.code, right?.action?.code),
      ],
    }),
  );
  const parameters = buildSopParameterDiff(before.parameters, after.parameters);
  const body = field('bodyMarkdown', before.bodyMarkdown, after.bodyMarkdown);
  const rules = field(
    'sessionRules',
    before.sessionRules ?? [],
    after.sessionRules ?? [],
  );
  return [
    ...(before.sessionRules?.length || after.sessionRules?.length
      ? [
          {
            id: 'sessionRules',
            kind: 'sessionRules',
            name: '',
            moved: false,
            status: rules.changed ? 'modified' : 'unchanged',
            fields: [rules],
          } satisfies SopDiffSection,
        ]
      : []),
    ...parameters,
    ...steps,
    {
      id: 'bodyMarkdown',
      kind: 'bodyMarkdown',
      name: '',
      moved: false,
      status: body.changed ? 'modified' : 'unchanged',
      fields: [body],
    },
  ];
}

/** Shared by version history and the unsaved Nami proposal preview. */
export function buildSopParameterDiff(
  before: AgentSopDto['parameters'],
  after: AgentSopDto['parameters'],
): SopDiffSection[] {
  return compareList(
    before,
    after,
    (param) => param.name,
    (left, right) => ({
      kind: 'parameter',
      name: right?.name ?? left?.name ?? '',
      fields: [field('parameter', left, right)],
    }),
  );
}
export function hasSopChanges(sections: SopDiffSection[]): boolean {
  return sections.some(
    (section) => section.status !== 'unchanged' || section.moved,
  );
}
export function defaultDiffBase(
  versions: AgentSopVersionEntry[],
  viewed: number,
): number {
  const current = versions.find((entry) => entry.version === viewed);
  const published = versions.find(
    (entry) => entry.isPinned && entry.version !== viewed,
  );
  const parent = versions.find(
    (entry) => entry.id === current?.parentVersionId,
  );
  const earlier = versions
    .filter((entry) => entry.version < viewed)
    .sort((a, b) => b.version - a.version)[0];
  return (
    published?.version ??
    parent?.version ??
    earlier?.version ??
    versions.find((entry) => entry.version !== viewed)?.version ??
    viewed
  );
}
export type SopDiffLayout = 'unified' | 'split';
export const SOP_DIFF_LAYOUT_KEY = 'sop-version-diff-layout';
export function initialDiffLayout(): SopDiffLayout {
  if (window.matchMedia?.('(max-width: 767px)').matches) return 'unified';
  try {
    return window.localStorage.getItem(SOP_DIFF_LAYOUT_KEY) === 'unified'
      ? 'unified'
      : 'split';
  } catch {
    return 'split';
  }
}
export function saveDiffLayout(layout: SopDiffLayout): void {
  try {
    window.localStorage.setItem(SOP_DIFF_LAYOUT_KEY, layout);
  } catch {
    /* Storage is optional. */
  }
}

interface SopComparisonRead {
  data?: SopDiffSnapshot & {
    version: number;
    versionStatus: 'draft' | 'finalized';
  };
  isError: boolean;
}
export function resolveDiffReads({
  before,
  after,
  versions,
  from,
  to,
}: {
  before: SopComparisonRead;
  after: SopComparisonRead;
  versions: AgentSopVersionEntry[];
  from: number;
  to: number;
}) {
  const available = [from, to].every((number) =>
    versions.some((version) => version.version === number),
  );
  if (!available || before.isError || after.isError) {
    return { state: 'error' as const, sections: [], targetIsDraft: false };
  }
  if (!before.data || !after.data) {
    return { state: 'loading' as const, sections: [], targetIsDraft: false };
  }
  if (before.data.version !== from || after.data.version !== to) {
    return { state: 'loading' as const, sections: [], targetIsDraft: false };
  }
  return {
    state: 'ready' as const,
    sections: buildSopVersionDiff(before.data, after.data),
    targetIsDraft: after.data.versionStatus === 'draft',
  };
}
